import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  SelectChangeEvent,
} from '@mui/material';
import { Add as AddIcon, ChevronRight as ChevronRightIcon, Circle as CircleIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { licensingAPI, apiService, License } from '../services/api';
import LicenseStatusBadge from '../components/common/LicenseStatusBadge';

const LicensesPage: React.FC = () => {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterType, setFilterType] = useState<'all' | 'song' | 'actor'>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    license_type: 'song' as 'song' | 'actor',
    expiry_date: null as Date | null,
    notes: '',
  });

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    loadLicenses();
  }, []);

  const loadLicenses = async () => {
    try {
      setLoading(true);
      setError(null);
      const { licenses: data } = await licensingAPI.list();
      setLicenses(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) return;

    try {
      setCreating(true);
      await licensingAPI.create({
        license_type: createForm.license_type,
        name: createForm.name.trim(),
        expiry_date: createForm.expiry_date ? format(createForm.expiry_date, 'yyyy-MM-dd') : null,
        notes: createForm.notes.trim() || null,
      });
      setSnackbarMessage('License created successfully');
      setSnackbarOpen(true);
      setCreateDialogOpen(false);
      setCreateForm({ name: '', license_type: 'song', expiry_date: null, notes: '' });
      await loadLicenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create license');
    } finally {
      setCreating(false);
    }
  };

  const handleTypeFilter = (_: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value !== null) {
      setFilterType(value as 'all' | 'song' | 'actor');
    }
  };

  const handleStatusFilter = (event: SelectChangeEvent<string>) => {
    setFilterStatus(event.target.value);
  };

  const filteredLicenses = licenses.filter((license) => {
    if (filterType !== 'all' && license.license_type !== filterType) return false;
    if (filterStatus !== 'all' && license.status !== filterStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!license.name.toLowerCase().includes(q) && !(license.notes || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const summary = {
    total: licenses.length,
    songs: licenses.filter(l => l.license_type === 'song').length,
    actors: licenses.filter(l => l.license_type === 'actor').length,
    expired: licenses.filter(l => l.status === 'expired').length,
    expiring: licenses.filter(l => l.status === 'expiring_soon').length,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Licenses</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create License
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5">{summary.total}</Typography>
                <Typography variant="body2" color="text.secondary">Total</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5">{summary.songs} / {summary.actors}</Typography>
                <Typography variant="body2" color="text.secondary">Songs / Actors</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5" color="warning.main">{summary.expiring}</Typography>
                <Typography variant="body2" color="text.secondary">Expiring Soon</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5" color="error.main">{summary.expired}</Typography>
                <Typography variant="body2" color="text.secondary">Expired</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 200 }}
          />

          <ToggleButtonGroup
            value={filterType}
            exclusive
            onChange={handleTypeFilter}
            size="small"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="song">Songs</ToggleButton>
            <ToggleButton value="actor">Actors</ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={handleStatusFilter}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expiring_soon">Expiring</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="no_expiry">No Expiry</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            Showing {filteredLicenses.length} of {licenses.length}
          </Typography>
        </Box>

        {/* Table */}
        {loading && <LinearProgress sx={{ mb: 1 }} />}
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell align="center">7d Spend</TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLicenses.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      {licenses.length === 0 ? 'No licenses yet' : 'No licenses match the current filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLicenses.map((license) => (
                  <TableRow
                    key={license.license_id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      borderLeft: 4,
                      borderColor: license.license_type === 'song' ? '#1976d2' : '#ed6c02',
                    }}
                    onClick={() => navigate(`/licenses/${license.license_id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {license.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {license.license_type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <LicenseStatusBadge status={license.status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {license.expiry_date || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <CircleIcon sx={{ fontSize: 12, color: license.has_activity_7d ? 'success.main' : 'grey.400' }} />
                    </TableCell>
                    <TableCell>
                      <ChevronRightIcon color="action" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create License Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create License</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="License Name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                required
                fullWidth
                placeholder="e.g. Let It Go - Frozen OST"
              />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={createForm.license_type}
                  label="Type"
                  onChange={(e) => setCreateForm({ ...createForm, license_type: e.target.value as 'song' | 'actor' })}
                >
                  <MenuItem value="song">Song</MenuItem>
                  <MenuItem value="actor">Actor</MenuItem>
                </Select>
              </FormControl>
              <DatePicker
                label="Expiry Date (optional)"
                value={createForm.expiry_date}
                onChange={(date) => setCreateForm({ ...createForm, expiry_date: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TextField
                label="Notes (optional)"
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={creating || !createForm.name.trim()}
            >
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default LicensesPage;
